import { create } from 'zustand';
import {
  createStorageAdapterWithFallback,
  detectRuntime,
  getAudioService,
  loadPlayerEconomyFromBackend,
  loadPlayerEconomy,
  isVkCommunityConfigured,
  requestAllowNotifications,
  requestJoinGroup,
  savePlayerEconomyToBackend,
  savePlayerEconomy,
  type StorageAdapter,
} from '../../infrastructure';
import type { CourierId } from '../../domain/ids/ids';
import { isCourierId } from '../../domain/ids/ids';
import type { ActionErrorCode } from './actionError';
import {
  beginDeliveryAttempt,
  createInitialPlayerEconomy,
  syncDeliveryPool,
  acknowledgeDailyPackComplete,
  ensureMissionInPool,
  findPoolEntry,
  isoDateFromMs,
  migrateDeliveryHistory,
  settleDeliveryAttempt,
  PASSES_CAP,
  type BeginDeliveryResult,
  type MissionRef,
  type PlayerEconomyState,
  type SettleDeliveryInput,
  type SettleDeliveryResult,
  submitQuizAnswer as submitQuizAnswerDomain,
  claimDailyLoginReward as claimDailyLoginRewardDomain,
  claimNotificationsReward as claimNotificationsRewardDomain,
  claimVkSubscribeReward as claimVkSubscribeRewardDomain,
  buildDailyLoginStateWithStreak,
  type QuizAnswerResult,
  type DailyLoginClaimResult,
  type VkBusinessTaskClaimResult,
  deliveryIdFromMission,
} from '../../domain';
import { preloadCriticalAssets } from '../assets/preloadCriticalAssets';
import { preloadSounds } from '../assets/preloadSounds';
import { type SessionAssetsStatus } from '../assets/preloadSessionLevelAssets';

export type HydrationStatus = 'loading' | 'ready' | 'error';
export type AssetsPreloadStatus = 'pending' | 'loading' | 'ready' | 'error';
export type AudioLevel = 0 | 1 | 2 | 3;

type AppStoreState = {
  hydrationStatus: HydrationStatus;
  hydrationError: string | null;
  onboardingComplete: boolean;
  selectedCourierId: CourierId | null;
  playerEconomy: PlayerEconomyState;
  passesBalance: number;
  audioLevel: AudioLevel;
  syncWarning: string | null;
  lastActionError: ActionErrorCode | null;
  lastActionErrorRatingRequired: number | null;
  lastRatingDelta: number | null;
  assetsPreloadStatus: AssetsPreloadStatus;
  assetsPreloadError: string | null;
  assetsPreloadProgress: { loaded: number; total: number } | null;
  sessionAssetsStatus: SessionAssetsStatus;
  bootstrap: () => Promise<void>;
  clearActionError: () => void;
  dismissSyncWarning: () => void;
  reportActionError: (code: ActionErrorCode, ratingRequired?: number) => void;
  setSelectedCourier: (id: CourierId) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  setAudioLevel: (level: AudioLevel) => Promise<void>;
  cycleAudioLevel: () => Promise<void>;
  refreshEconomy: () => Promise<void>;
  ensureAndBeginDelivery: (mission: MissionRef) => Promise<BeginDeliveryResult>;
  settleDelivery: (input: SettleDeliveryInput) => Promise<SettleDeliveryResult>;
  acknowledgeDailyPackCompleteModal: () => Promise<void>;
  submitQuizAnswer: (questionId: string, selectedIndex: number) => Promise<QuizAnswerResult>;
  claimDailyLoginReward: () => Promise<DailyLoginClaimResult>;
  completeNotificationsTask: () => Promise<VkBusinessTaskClaimResult & { completed: boolean }>;
  completeVkSubscribeTask: () => Promise<VkBusinessTaskClaimResult & { completed: boolean }>;
  resetDailyLoginProgress: () => Promise<void>;
  seedDailyLoginStreakForDev: (streakDays: number) => Promise<void>;
  resetPlayerProgressForAdmin: () => Promise<void>;
  grantPassesForAdmin: (amount?: number) => Promise<void>;
};

const ADMIN_PASS_GRANT_AMOUNT = 5;

let persistence: StorageAdapter | null = null;
let bootstrapInflight: Promise<void> | null = null;

function bootstrapErrorMessage(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === 'object' && e !== null) {
    const vk = e as {
      error_msg?: string;
      error_data?: { error_msg?: string };
    };
    if (typeof vk.error_msg === 'string' && vk.error_msg !== '') {
      return vk.error_msg;
    }
    const nested = vk.error_data?.error_msg;
    if (typeof nested === 'string' && nested !== '') {
      return nested;
    }
    try {
      return JSON.stringify(e);
    } catch {
      return String(e);
    }
  }
  return String(e);
}

function parseOnboardingComplete(raw: string | null): boolean {
  if (raw == null || raw === '') {
    return false;
  }
  return raw === '1' || raw === 'true';
}

function isAudioLevel(value: number): value is AudioLevel {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

function parseAudioLevel(
  rawLevel: string | null,
  rawMuted: string | null,
): AudioLevel {
  if (rawLevel != null && rawLevel !== '') {
    const parsed = Number(rawLevel);
    if (isAudioLevel(parsed)) {
      return parsed;
    }
  }
  if (rawMuted === '1' || rawMuted === 'true') {
    return 0;
  }
  return 3;
}

function applyAudioLevel(level: AudioLevel): void {
  const audio = getAudioService();
  audio.setMuted(level === 0);
  audio.setMasterVolume(level / 3);
}

function nextAudioLevel(level: AudioLevel): AudioLevel {
  return ((level + 1) % 4) as AudioLevel;
}

const now = () => Date.now();

function sliceFromEconomy(economy: PlayerEconomyState) {
  return { playerEconomy: economy, passesBalance: economy.passesBalance };
}

async function persistEconomy(economy: PlayerEconomyState): Promise<void> {
  if (!persistence) {
    throw new Error('persistEconomy: persistence is not ready');
  }
  try {
    await savePlayerEconomy(persistence, economy);
    if (useAppStore.getState().lastActionError === 'save_failed') {
      useAppStore.setState({ lastActionError: null, lastActionErrorRatingRequired: null });
    }
  } catch (e) {
    console.warn('[presentation] local save failed, keeping in-memory state', e);
    useAppStore.setState({
      lastActionError: 'save_failed',
      lastActionErrorRatingRequired: null,
    });
  }
  try {
    await savePlayerEconomyToBackend(economy);
    useAppStore.setState({ syncWarning: null });
  } catch (e) {
    console.warn('[presentation] backend save failed, keeping local state only', e);
    useAppStore.setState({ syncWarning: syncWarningMessage('backend_save_failed') });
  }
}

function syncWarningMessage(code: string): string {
  if (code === 'backend_save_failed' || code === 'backend_load_failed') {
    return 'Синхронизация с сервером недоступна. Играем в локальном режиме.';
  }
  return code;
}

const initialEconomy = createInitialPlayerEconomy(0);

const initialSlice = {
  hydrationStatus: 'loading' as HydrationStatus,
  hydrationError: null as string | null,
  onboardingComplete: false,
  selectedCourierId: null as CourierId | null,
  playerEconomy: initialEconomy,
  passesBalance: 0,
  syncWarning: null as string | null,
  lastActionError: null as ActionErrorCode | null,
  lastActionErrorRatingRequired: null as number | null,
  lastRatingDelta: null as number | null,
  audioLevel: 3 as AudioLevel,
  assetsPreloadStatus: 'pending' as AssetsPreloadStatus,
  assetsPreloadError: null as string | null,
  assetsPreloadProgress: null as { loaded: number; total: number } | null,
  sessionAssetsStatus: 'pending' as SessionAssetsStatus,
};

export const useAppStore = create<AppStoreState>((set, get) => ({
  ...initialSlice,

  bootstrap: async () => {
    if (bootstrapInflight != null) {
      return bootstrapInflight;
    }
    bootstrapInflight = (async () => {
      set({
        ...initialSlice,
        hydrationStatus: 'loading',
        hydrationError: null,
        syncWarning: null,
        lastActionError: null,
        lastActionErrorRatingRequired: null,
        lastRatingDelta: null,
        assetsPreloadStatus: 'pending',
        assetsPreloadError: null,
        assetsPreloadProgress: null,
        sessionAssetsStatus: 'pending',
      });
      try {
        const runtime = detectRuntime();
        persistence = await createStorageAdapterWithFallback(runtime.kind);
        const [rawOnboarding, rawCourier, rawPasses, rawAudioLevel, rawAudioMuted] =
          await Promise.all([
          persistence.get('onboarding_complete'),
          persistence.get('selected_courier_id'),
          persistence.get('passes_balance'),
          persistence.get('audio_level'),
          persistence.get('audio_muted'),
        ]);
        const onboardingComplete = parseOnboardingComplete(rawOnboarding);
        const audioLevel = parseAudioLevel(rawAudioLevel, rawAudioMuted);
        applyAudioLevel(audioLevel);
        if (rawAudioLevel == null || rawAudioLevel === '') {
          try {
            await persistence.set('audio_level', String(audioLevel));
          } catch (saveError) {
            console.warn('[presentation] audio_level migration save failed', saveError);
          }
        }
        let selectedCourierId: CourierId | null = null;
        if (rawCourier != null && rawCourier !== '' && isCourierId(rawCourier)) {
          selectedCourierId = rawCourier;
        }
        const economy = await loadPlayerEconomy(persistence, rawPasses, now());
        let syncedEconomy = economy;
        let syncWarning: string | null = null;
        try {
          const backendEconomy = await loadPlayerEconomyFromBackend();
          if (backendEconomy != null) {
            syncedEconomy = migrateDeliveryHistory(syncDeliveryPool(backendEconomy, now()));
            try {
              await savePlayerEconomy(persistence, syncedEconomy);
            } catch (saveError) {
              console.warn(
                '[presentation] local save of backend economy failed',
                saveError,
              );
            }
          }
        } catch (e) {
          console.warn('[presentation] backend load failed, using local state only', e);
          syncWarning = syncWarningMessage('backend_load_failed');
        }
        set({
          onboardingComplete,
          selectedCourierId,
          audioLevel,
          hydrationStatus: 'ready',
          hydrationError: null,
          syncWarning,
          lastActionError: null,
          lastActionErrorRatingRequired: null,
          lastRatingDelta: null,
          ...sliceFromEconomy(syncedEconomy),
          assetsPreloadStatus: 'loading',
          assetsPreloadError: null,
          assetsPreloadProgress: null,
          sessionAssetsStatus: 'pending',
        });
        preloadSounds();
        try {
          const preloadResult = await preloadCriticalAssets({
            onProgress: (loaded, total) => {
              useAppStore.setState({
                assetsPreloadProgress: { loaded, total },
              });
            },
          });
          if (preloadResult.failedUrls.length > 0) {
            console.warn(
              '[presentation] some assets failed to preload',
              preloadResult.failedUrls.length,
            );
          }
          set({
            assetsPreloadStatus: 'ready',
            assetsPreloadError: null,
            assetsPreloadProgress: {
              loaded: preloadResult.loaded,
              total: preloadResult.total,
            },
            sessionAssetsStatus: 'ready',
          });
        } catch (e) {
          const message = bootstrapErrorMessage(e);
          console.error('[presentation] asset preload failed', e);
          set({
            assetsPreloadStatus: 'ready',
            assetsPreloadError: message,
            sessionAssetsStatus: 'ready',
          });
        }
      } catch (e) {
        const message = bootstrapErrorMessage(e);
        console.error('[presentation] bootstrap failed', e);
        persistence = null;
        set({
          ...initialSlice,
          hydrationStatus: 'error',
          hydrationError: message,
          assetsPreloadStatus: 'error',
          assetsPreloadError: message,
        });
      }
    })();
    try {
      await bootstrapInflight;
    } finally {
      bootstrapInflight = null;
    }
  },

  setSelectedCourier: async (id: CourierId) => {
    if (!persistence) {
      throw new Error('setSelectedCourier: persistence is not ready');
    }
    await persistence.set('selected_courier_id', id);
    set({ selectedCourierId: id });
  },

  completeOnboarding: async () => {
    if (!persistence) {
      throw new Error('completeOnboarding: persistence is not ready');
    }
    await persistence.set('onboarding_complete', '1');
    set({ onboardingComplete: true });
  },

  setAudioLevel: async (level: AudioLevel) => {
    applyAudioLevel(level);
    set({ audioLevel: level });
    if (!persistence) {
      return;
    }
    await persistence.set('audio_level', String(level));
  },

  cycleAudioLevel: async () => {
    const next = nextAudioLevel(get().audioLevel);
    await get().setAudioLevel(next);
  },

  clearActionError: () => {
    set({ lastActionError: null, lastActionErrorRatingRequired: null });
  },

  dismissSyncWarning: () => {
    set({ syncWarning: null });
  },

  reportActionError: (code, ratingRequired) => {
    set({
      lastActionError: code,
      lastActionErrorRatingRequired:
        ratingRequired != null ? ratingRequired : null,
    });
  },

  refreshEconomy: async () => {
    const synced = syncDeliveryPool(get().playerEconomy, now());
    await persistEconomy(synced);
    set(sliceFromEconomy(synced));
  },

  acknowledgeDailyPackCompleteModal: async () => {
    const synced = acknowledgeDailyPackComplete(get().playerEconomy, isoDateFromMs(now()));
    await persistEconomy(synced);
    set(sliceFromEconomy(synced));
  },

  ensureAndBeginDelivery: async (mission: MissionRef) => {
    const t = now();
    let economy = syncDeliveryPool(get().playerEconomy, t);
    economy = ensureMissionInPool(economy, mission, t);
    const poolEntry = findPoolEntry(economy, deliveryIdFromMission(mission));
    if (poolEntry == null) {
      set({ lastActionError: 'not_in_pool', lastActionErrorRatingRequired: null });
      return { ok: false as const, reason: 'not_in_pool' as const, state: economy };
    }
    const result = beginDeliveryAttempt(economy, mission, t);
    if (result.ok) {
      await persistEconomy(result.state);
      set({
        ...sliceFromEconomy(result.state),
        lastActionError: null,
        lastActionErrorRatingRequired: null,
        lastRatingDelta: null,
      });
    } else {
      set({ lastActionError: result.reason, lastActionErrorRatingRequired: null });
    }
    return result;
  },

  settleDelivery: async (input: SettleDeliveryInput) => {
    const result = settleDeliveryAttempt(get().playerEconomy, input);
    await persistEconomy(result.state);
    const lastRatingDelta = input.outcome === 'completed' ? result.ratingDelta : get().lastRatingDelta;
    set({ ...sliceFromEconomy(result.state), lastRatingDelta });
    return result;
  },

  submitQuizAnswer: async (questionId: string, selectedIndex: number): Promise<QuizAnswerResult> => {
    const prev = get().playerEconomy;
    const result = submitQuizAnswerDomain(prev, questionId, selectedIndex);
    if (result.state !== prev) {
      await persistEconomy(result.state);
      set(sliceFromEconomy(result.state));
    }
    return result;
  },

  claimDailyLoginReward: async (): Promise<DailyLoginClaimResult> => {
    const result = claimDailyLoginRewardDomain(get().playerEconomy);
    if (!result.alreadyClaimed && result.rewardPasses > 0) {
      await persistEconomy(result.state);
      set(sliceFromEconomy(result.state));
    }
    return result;
  },

  completeNotificationsTask: async () => {
    const prev = get().playerEconomy;
    if (prev.notificationsRewardClaimed === true) {
      get().reportActionError('already_claimed');
      return { alreadyClaimed: true, state: prev, rewardPasses: 0, completed: true };
    }
    if (detectRuntime().kind !== 'vk_mini_app') {
      get().reportActionError('vk_unavailable');
      return { alreadyClaimed: false, state: prev, rewardPasses: 0, completed: false };
    }
    const allowed = await requestAllowNotifications();
    if (!allowed) {
      get().reportActionError('notifications_denied');
      return { alreadyClaimed: false, state: prev, rewardPasses: 0, completed: false };
    }
    const result = claimNotificationsRewardDomain(get().playerEconomy);
    if (!result.alreadyClaimed && result.rewardPasses > 0) {
      await persistEconomy(result.state);
      set({
        ...sliceFromEconomy(result.state),
        lastActionError: null,
        lastActionErrorRatingRequired: null,
      });
    }
    return { ...result, completed: true };
  },

  completeVkSubscribeTask: async () => {
    const prev = get().playerEconomy;
    if (prev.vkSubscribeRewardClaimed === true) {
      get().reportActionError('already_claimed');
      return { alreadyClaimed: true, state: prev, rewardPasses: 0, completed: true };
    }
    if (detectRuntime().kind !== 'vk_mini_app') {
      get().reportActionError('vk_unavailable');
      return { alreadyClaimed: false, state: prev, rewardPasses: 0, completed: false };
    }
    if (!isVkCommunityConfigured()) {
      get().reportActionError('vk_not_configured');
      return { alreadyClaimed: false, state: prev, rewardPasses: 0, completed: false };
    }
    const joined = await requestJoinGroup();
    if (!joined) {
      get().reportActionError('vk_subscribe_failed');
      return { alreadyClaimed: false, state: prev, rewardPasses: 0, completed: false };
    }
    const result = claimVkSubscribeRewardDomain(get().playerEconomy);
    if (!result.alreadyClaimed && result.rewardPasses > 0) {
      await persistEconomy(result.state);
      set({
        ...sliceFromEconomy(result.state),
        lastActionError: null,
        lastActionErrorRatingRequired: null,
      });
    }
    return { ...result, completed: true };
  },

  resetDailyLoginProgress: async (): Promise<void> => {
    const prev = get().playerEconomy;
    const state: PlayerEconomyState = {
      ...prev,
      streakDays: 0,
      lastLoginDate: null,
      lastDailyRewardClaimedDate: null,
      dailyLoginStreakCompleted: false,
    };
    await persistEconomy(state);
    set(sliceFromEconomy(state));
  },

  seedDailyLoginStreakForDev: async (streakDays: number): Promise<void> => {
    const prev = get().playerEconomy;
    const state = buildDailyLoginStateWithStreak(prev, streakDays);
    await persistEconomy(state);
    set(sliceFromEconomy(state));
  },

  resetPlayerProgressForAdmin: async (): Promise<void> => {
    const now = Date.now();
    const economy = createInitialPlayerEconomy(now, {
      packSeedExtra: `${now}-${Math.random()}`,
    });
    await persistEconomy(economy);
    set({ ...sliceFromEconomy(economy), lastRatingDelta: null });
  },

  grantPassesForAdmin: async (amount = ADMIN_PASS_GRANT_AMOUNT): Promise<void> => {
    const grant = Math.max(0, Math.floor(amount));
    if (grant === 0) {
      return;
    }
    const prev = get().playerEconomy;
    const state: PlayerEconomyState = {
      ...prev,
      passesBalance: Math.min(PASSES_CAP, prev.passesBalance + grant),
    };
    await persistEconomy(state);
    set(sliceFromEconomy(state));
  },
}));

/** Сброс для тестов (не вызывать из прод-кода). */
export function __resetAppStoreForTests(): void {
  persistence = null;
  bootstrapInflight = null;
  useAppStore.setState(initialSlice);
}

/** Тестовый доступ к адаптеру персистентности. */
export function __getPersistenceForTests(): StorageAdapter | null {
  return persistence;
}
